import { useMemo, useState } from "react";
import {
  Alert, Box, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableRow, TextField as MuiTextField, Typography, } from "@mui/material";
import taskerService, {
  type TaskerTask, } from "../../../services/taskerService";
import { readErrorMessage } from "../utils/mapBeErrors";
import { ReviewerActionsFooter } from "./ReviewerActionsFooter";
import { useRemarksMaxLength } from "./useRemarksMaxLength";
import { CharCounter } from "../components/CharCounter";
import { clampText } from "../utils/textLimits";
import { useLatestFormData, useReviewerTaskType } from "./reviewerQueries";
import { stepForms as parseStepForms, transitionForms as parseTransitionForms } from "./taskConfig";
import { RiskScoreCard } from "./RiskScoreCard";
import { PriorReviewerRemarks } from "./PriorReviewerRemarks";
import {
  REVIEWER_APPROVED_MSG, REVIEWER_REJECTED_MSG, REVIEWER_SEND_BACK_MSG_TO_VM, REVIEWER_REMARKS_FIELD, reviewerFormId } from "./reviewerRoles";
import { computeVendorRiskScore } from "./riskScore";
import { type OnTransitionSubmitted } from "./ReviewerRouter";
import { MIN_COMPLIANCE_VOLUME } from "./complianceGate";

type Props = { task: TaskerTask; onSubmitted: OnTransitionSubmitted };

type CheckResult = {
  label: string;
  outcome: string;
  tone: "pass" | "fail" | "info";
};

// Illustrative fixtures for the v1 Compliance surface. Real backend checks
// (PAN dedupe, GST API status, bank penny-drop, address reuse, OCR recon,
// PAN/GSTIN watchlist, duplicate detection) are targeted for v2 — the
// integration lands as part of ENG-22808's v2 phase. Rendered as tone:"pass"
// to match the demo/preview shape the design + product review signed off on.
const STUB_CHECKS: CheckResult[] = [
  { label: "PAN verification (IT + HRMS)", outcome: "Verified", tone: "pass" },
  { label: "GST status (GST API)", outcome: "Active", tone: "pass" },
  { label: "Bank penny-drop", outcome: "Name match · Success", tone: "pass" },
  { label: "Address reuse check", outcome: "No active-vendor match", tone: "pass" },
  { label: "OCR document reconciliation", outcome: "Matched", tone: "pass" },
  { label: "PAN / GSTIN watchlist", outcome: "Clear", tone: "pass" },
  {
    label: "Duplicate detection (PAN/GST/bank/email/phone)",
    outcome: "No duplicates",
    tone: "pass",
  },
];

const RULES_FOOTER =
  "Rules: GST Cancelled → REJECT_REQUEST · GST non-filing >6mo → BLOCK · GST Suspended / address-reuse / watchlist → ROUTE_TO_MANUAL_REVIEW.";

function ChipForOutcome({ result }: { result: CheckResult }) {
  const palette = {
    pass: { bg: "#E6F4EA", fg: "#1B7C4E" },
    fail: { bg: "#FDECEA", fg: "#B71C1C" },
    info: { bg: "#F0F2F8", fg: "text.secondary" },
  }[result.tone];
  return (
    <Chip
      size="small"
      label={result.outcome}
      sx={{
        bgcolor: palette.bg,
        color: palette.fg,
        fontWeight: 700,
        fontSize: 11,
      }}
    />
  );
}

export function CompliancePanel({ task, onSubmitted }: Props) {
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const taskTypeQuery = useReviewerTaskType(task.task_type_id);
  const remarksMaxLength = useRemarksMaxLength(
    reviewerFormId(taskTypeQuery.data?.task_config, "compliance"),
    REVIEWER_REMARKS_FIELD.compliance,
  );

  const stepForms = parseStepForms(taskTypeQuery.data?.task_config);
  const businessDetailsFormId = stepForms?.STEP_1_BUSINESS_DETAILS;
  const branchesFormId = stepForms?.STEP_2_BRANCHES;
  const vmEnrichmentFormId = parseTransitionForms(
    taskTypeQuery.data?.task_config,
  )?.vm_enrichment;

  const businessDetailsQuery = useLatestFormData(
    "reviewer-business-details",
    task.id,
    businessDetailsFormId,
  );

  const branchesQuery = useLatestFormData(
    "reviewer-compliance-branches",
    task.id,
    branchesFormId,
  );

  // Not shown on this panel; the Risk Assessment card scores it.
  const vmEnrichmentQuery = useLatestFormData(
    "reviewer-compliance-vm-enrichment",
    task.id,
    vmEnrichmentFormId,
  );

  const businessDetails = businessDetailsQuery.data ?? {};

  const riskResult = useMemo(() => {
    const rawBranches = branchesQuery.data?.branches;
    const branches = Array.isArray(rawBranches)
      ? (rawBranches as Record<string, unknown>[])
      : [];
    return computeVendorRiskScore(
      businessDetailsQuery.data,
      vmEnrichmentQuery.data,
      branches,
    );
  }, [businessDetailsQuery.data, branchesQuery.data, vmEnrichmentQuery.data]);
  const volume = Number(businessDetails.expected_business_volume ?? NaN);
  const volumeLabel = Number.isFinite(volume)
    ? `₹${volume.toLocaleString("en-IN")}`
    : "—";
  const volumeGateTriggered =
    Number.isFinite(volume) && volume >= MIN_COMPLIANCE_VOLUME;

  const runTransition = async (
    action:
      | "compliance_approve"
      | "compliance_reject"
      | "compliance_go_back",
    reason?: string,
  ) => {
    if (!task.id) return;
    setSubmitting(true);
    setError(null);
    try {
      const decision =
        action === "compliance_approve"
          ? "Approve"
          : action === "compliance_go_back"
          ? "Send Back"
          : "Reject";
      const form_data = {
        compliance_decision: decision,
        compliance_remarks: reason ?? remarks,
      };
      await taskerService.executeTransition(
        task.id,
        action,
        form_data,
        "reviewer",
      );
      const outcome = action === "compliance_approve" ? "approved" : "rejected";
      const message =
        action === "compliance_approve"
          ? REVIEWER_APPROVED_MSG.compliance
          : action === "compliance_go_back"
          ? REVIEWER_SEND_BACK_MSG_TO_VM
          : REVIEWER_REJECTED_MSG;
      onSubmitted(outcome, message);
    } catch (err) {
      setError(readErrorMessage(err, "Couldn't submit decision. Try again."));
      // Re-throw so reject/send-back callers (the footer's async confirm)
      // see the failure and keep the dialog open with the typed reason.
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = () => {
    // Approve doesn't come from a dialog with a reason to preserve — the
    // panel-level error banner is enough.
    void runTransition("compliance_approve").catch(() => {});
  };

  const handleReject = (reason: string) =>
    runTransition("compliance_reject", reason);

  const handleSendBack = (reason: string) =>
    runTransition("compliance_go_back", reason);

  const stubChecks = useMemo(() => STUB_CHECKS, []);

  // Risk Assessment card renders a definitive score — it must not compose
  // from partial input. This includes the disabled-query case: every form-id
  // here is derived from taskTypeQuery.data?.task_config, so when taskTypeQuery
  // errors OR task_config lacks a required key, the dependent queries are
  // enabled:false (neither loading nor errored) and would otherwise render a
  // fabricated LOW RISK next to Approve · Activate. isInitialLoading is
  // (isLoading && isFetching) so a disabled query's isFetching:false slips past
  // the spinner. See priorSteps.tsx for the same pattern.
  const riskDataMissing =
    taskTypeQuery.isError ||
    branchesQuery.isError ||
    vmEnrichmentQuery.isError ||
    businessDetailsQuery.isError ||
    !businessDetailsFormId ||
    !branchesFormId ||
    !vmEnrichmentFormId;

  if (
    taskTypeQuery.isInitialLoading ||
    businessDetailsQuery.isInitialLoading ||
    branchesQuery.isInitialLoading ||
    vmEnrichmentQuery.isInitialLoading
  ) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 0.5,
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
          Compliance review
        </Typography>
        {volumeGateTriggered && (
          <Chip
            label={`REQUIRED · ≥ ₹${(MIN_COMPLIANCE_VOLUME / 100000).toLocaleString("en-IN")}L`}
            sx={{
              bgcolor: "#EEB142",
              color: "#1F1300",
              fontWeight: 700,
              fontSize: 11,
              height: 26,
              letterSpacing: 0.3,
              borderRadius: "8px",
              "& .MuiChip-label": { px: 2 },
            }}
          />
        )}
      </Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
        Expected business volume {volumeLabel}
        {businessDetailsQuery.isError || !businessDetailsFormId
          ? " — Step 1 business details not resolved; treat gate as unknown."
          : volumeGateTriggered
          ? ` → compliance gate triggered (≥ ₹${(MIN_COMPLIANCE_VOLUME / 100000).toLocaleString("en-IN")}L, system-derived).`
          : ` → below the ₹${(MIN_COMPLIANCE_VOLUME / 100000).toLocaleString("en-IN")}L gate; task should not typically reach this stage.`}
      </Typography>

      <PriorReviewerRemarks
        task={task}
        taskConfig={taskTypeQuery.data?.task_config}
        authMode="reviewer"
        excludeRole="compliance"
      />

      {(businessDetailsQuery.isError || !businessDetailsFormId) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Couldn&apos;t resolve Step 1 business details — the volume gate is
          unknown; don&apos;t treat the score below as authoritative.
        </Alert>
      )}

      {riskDataMissing ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Couldn&apos;t load one or more inputs to the risk score (business
          details / branches / VM enrichment). Refresh to retry; do not treat
          this stage as a pass without a computed score.
        </Alert>
      ) : (
        <RiskScoreCard result={riskResult} />
      )}

      <Paper
        variant="outlined"
        sx={{ borderColor: "#E0E4EF", borderRadius: "8px", mb: 2 }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: "#E0E4EF",
            bgcolor: "#FAFBFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            Validation outcome summary
          </Typography>
          <Chip
            size="small"
            label="Illustrative · checks not yet wired (v2)"
            sx={{
              bgcolor: "#FEF7E0",
              color: "#B7791F",
              fontSize: 10,
              height: 20,
              fontWeight: 600,
            }}
          />
        </Box>
        <Table size="small" sx={{ "& td": { borderColor: "#E0E4EF" } }}>
          <TableBody>
            {stubChecks.map((check) => (
              <TableRow key={check.label}>
                <TableCell
                  sx={{ width: "70%", color: "text.secondary", fontSize: 13, py: 1.25 }}
                >
                  {check.label}
                </TableCell>
                <TableCell align="right" sx={{ py: 1.25 }}>
                  <ChipForOutcome result={check} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: "#E0E4EF" }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {RULES_FOOTER}
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>
          Compliance remarks
        </Typography>
        <MuiTextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          value={remarks}
          onChange={(e) => setRemarks(clampText(e.target.value, remarksMaxLength))}
          placeholder="Optional for approve; reject collects a mandatory reason separately."
          inputProps={{ autoComplete: "compliance-remarks", maxLength: remarksMaxLength }}
          sx={{
            "& .MuiInputBase-input": { fontSize: 13 },
          }}
        />
        <CharCounter length={remarks.length} max={remarksMaxLength} />
      </Box>

      <ReviewerActionsFooter
        reasonMaxLength={remarksMaxLength}
        submitting={submitting}
        errorMessage={error}
        onApprove={handleApprove}
        onReject={handleReject}
        onSendBack={handleSendBack}
        approveLabel="Approve · Activate"
        rejectPromptTitle="Reject onboarding"
        sendBackPromptTitle="Send back to Vendor Manager"
        sendBackPromptPlaceholder="Reason to send back (Vendor Manager will see this)"
      />
    </Box>
  );
}

export default CompliancePanel;
